import { useRef, useState, useContext, forwardRef, useImperativeHandle } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Users } from "react-feather";
import { addFamiliesToCampaign } from "../../../services/campaign";
import NotyfContext from "../../../contexts/NotyfContext";

const AddFamilies = forwardRef(({ afterSubmit, campaignId }, ref) => {
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const notyf = useContext(NotyfContext);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check if file is CSV
      if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith('.csv')) {
        notyf.error("Please select a valid CSV file");
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      notyf.error("Please select a CSV file");
      return;
    }

    if (!campaignId) {
      notyf.error("Campaign ID is required");
      return;
    }

    setLoading(true);
    try {
      const result = await addFamiliesToCampaign(campaignId, file);
      console.log(result);
      notyf.success(result.message || `Successfully added ${result.addedFamiliesCount} families to campaign`);
      setShow(false);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (afterSubmit) {
        await afterSubmit();
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || 
        error?.[0]?.description || 
        error.message || 
        "Something went wrong";
      notyf.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useImperativeHandle(ref, () => ({
    setShow: (show) => setShow(show)
  }));

  return (
    <>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Families to Campaign</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>CSV File with Family IDs</Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                required
              />
              <Form.Text className="text-muted">
                Upload a CSV file containing family IDs to add to the campaign. 
                The CSV should have one family ID per row.
              </Form.Text>
            </Form.Group>
            {file && (
              <div className="mt-2">
                <small className="text-success">
                  Selected file: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </small>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || !file}
            >
              {loading ? "Adding Families..." : "Add Families"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
});

export default AddFamilies; 